import React from 'react';
import {
    AppBar,
    Toolbar,
    Typography,
    IconButton,
    Box,
    useTheme,
    useMediaQuery,
    Menu,
    MenuItem,
    Avatar,
    Button,
} from '@mui/material';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import HomeIcon from '@mui/icons-material/Home';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import LogoutIcon from '@mui/icons-material/Logout';
import PersonIcon from '@mui/icons-material/Person';
import LanguageSwitcher from './LanguageSwitcher';
import MarketDataTicker from './MarketDataTicker';
import MobileMarketTicker from './MobileMarketTicker';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

interface NavigationHeaderProps {
    isDarkMode: boolean;
    onToggleTheme: () => void;
}

const NavigationHeader: React.FC<NavigationHeaderProps> = ({
    isDarkMode,
    onToggleTheme,
}) => {
    const { t, i18n } = useTranslation();
    const { user, isAuthenticated, logout } = useAuth();
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const isRTL = i18n.language === 'fa';
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

    const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleLogout = async () => {
        try {
            await logout();
            handleMenuClose();
            navigate('/');
        } catch (error) {
            console.error('Logout error:', error);
        }
    };

    const handleProfile = () => {
        handleMenuClose();
        navigate('/profile');
    };

    const handleLogin = () => {
        navigate('/auth');
    };

    const handleLogoClick = () => {
        navigate('/');
    };

    // Determine if user is a guest (not authenticated)
    const isGuest = !isAuthenticated;

    return (
        <>
            <AppBar position="static" elevation={1}>
                <Toolbar>
                    <Box 
                        sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            flexGrow: 1,
                            cursor: 'pointer',
                            '&:hover': {
                                opacity: 0.8
                            }
                        }}
                        onClick={handleLogoClick}
                    >
                        <HomeIcon sx={{ mr: 1 }} />
                        <Typography variant="h6" component="div">
                            P2P Exchange
                        </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" sx={{ mr: 1, display: { xs: 'none', sm: 'block' } }}>
                            {isGuest ? t('nav.guest') : (user?.displayName || user?.email || t('nav.dashboard'))}
                        </Typography>
                        <LanguageSwitcher />
                        <IconButton onClick={onToggleTheme} color="inherit">
                            {isDarkMode ? <Brightness7Icon /> : <Brightness4Icon />}
                        </IconButton>
                        
                        {/* User Menu or Login Button */}
                        {isAuthenticated ? (
                            <>
                                <IconButton
                                    onClick={handleMenuOpen}
                                    color="inherit"
                                    sx={{ ml: 1 }}
                                >
                                    <Avatar sx={{ width: 32, height: 32, bgcolor: 'secondary.main' }}>
                                        {user?.displayName ? user.displayName.charAt(0).toUpperCase() : user?.email?.charAt(0).toUpperCase()}
                                    </Avatar>
                                </IconButton>
                                <Menu
                                    anchorEl={anchorEl}
                                    open={Boolean(anchorEl)}
                                    onClose={handleMenuClose}
                                    anchorOrigin={{
                                        vertical: 'bottom',
                                        horizontal: 'right',
                                    }}
                                    transformOrigin={{
                                        vertical: 'top',
                                        horizontal: 'right',
                                    }}
                                >
                                    <MenuItem onClick={handleProfile}>
                                        <PersonIcon sx={{ mr: 1 }} />
                                        {t('user.profile')}
                                    </MenuItem>
                                    <MenuItem onClick={handleLogout}>
                                        <LogoutIcon sx={{ mr: 1 }} />
                                        {t('user.logout')}
                                    </MenuItem>
                                </Menu>
                            </>
                        ) : (
                            <Button
                                color="inherit"
                                onClick={handleLogin}
                                startIcon={<AccountCircleIcon />}
                                sx={{ ml: 1 }}
                            >
                                {t('auth.login')}
                            </Button>
                        )}
                    </Box>
                </Toolbar>
            </AppBar>

            {/* Market Data Ticker - Show for all users */}
            <>
                {/* Market Data Ticker - Desktop */}
                {!isMobile && (
                    <Box
                        sx={{
                            backgroundColor: theme.palette.mode === 'dark'
                                ? 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)'
                                : 'linear-gradient(135deg, #f5f5f5 0%, #e8e8e8 100%)',
                            borderBottom: `1px solid ${theme.palette.divider}`,
                            py: 1,
                            px: 2,
                            overflow: 'hidden',
                            position: 'relative',
                            '&::before': {
                                content: '""',
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                height: '1px',
                                background: theme.palette.mode === 'dark'
                                    ? 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.1) 50%, transparent 100%)'
                                    : 'linear-gradient(90deg, transparent 0%, rgba(0,0,0,0.1) 50%, transparent 100%)',
                            },
                        }}
                    >
                        <MarketDataTicker isRTL={isRTL} />
                    </Box>
                )}

                {/* Market Data Ticker - Mobile */}
                {isMobile && (
                    <Box sx={{ p: 2 }}>
                        <MobileMarketTicker isRTL={isRTL} />
                    </Box>
                )}
            </>
        </>
    );
};

export default NavigationHeader; 